import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { usePermission } from '@/lib/permissions';
import { extractErrorMessage } from '@/lib/errors';
import {
  preferencesApi,
  type NotificationPreference,
} from '@/features/notifications/preferences-api';
import {
  NOTIFICATION_CATALOG,
  type CatalogItem,
} from '@/features/notifications/notification-catalog';
import { SchedulePopover } from '@/features/notifications/schedule-popover';
import { SendHourSelect } from '@/features/notifications/send-hour-select';
import { useSchedules, useUpdateSchedule } from '@/features/notifications/use-schedules';

export const Route = createFileRoute('/settings/notifications')({
  beforeLoad: () => {
    throw redirect({ to: '/settings', search: { tab: 'notifications' } });
  },
});

const CHANNELS = [
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'in_app', label: 'In-app', icon: Bell },
] as const;

type ChannelKey = (typeof CHANNELS)[number]['key'];

// Build an O(1) lookup keyed by `${channel}|${eventType}`.
function buildLookup(preferences: NotificationPreference[]): Map<string, boolean> {
  const map = new Map<string, boolean>();
  for (const p of preferences) {
    map.set(`${p.channel}|${p.eventType}`, p.enabled);
  }
  return map;
}

const isEnabled = (lookup: Map<string, boolean>, channel: string, eventType: string): boolean =>
  lookup.get(`${channel}|${eventType}`) ?? true; // default ON when no row exists

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const canConfigure = usePermission('notifications.configure');

  const { data: preferences = [], isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: preferencesApi.getAll,
  });

  const lookup = useMemo(() => buildLookup(preferences), [preferences]);

  const { data: schedules = [] } = useSchedules({ enabled: canConfigure });
  const digest = schedules.find((s) => s.key === 'whos_out_digest');
  const updateSchedule = useUpdateSchedule();

  const updatePref = useMutation({
    mutationFn: preferencesApi.update,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['notification-preferences'] });

      const previous = queryClient.getQueryData<NotificationPreference[]>([
        'notification-preferences',
      ]);

      queryClient.setQueryData<NotificationPreference[]>(
        ['notification-preferences'],
        (old = []) => {
          const existing = old.find(
            (p) => p.channel === variables.channel && p.eventType === variables.eventType
          );
          if (existing) {
            return old.map((p) =>
              p.channel === variables.channel && p.eventType === variables.eventType
                ? { ...p, enabled: variables.enabled }
                : p
            );
          }
          return [
            ...old,
            {
              id: `temp-${variables.channel}-${variables.eventType}`,
              employeeId: '',
              channel: variables.channel,
              eventType: variables.eventType,
              enabled: variables.enabled,
            },
          ];
        }
      );

      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['notification-preferences'], context.previous);
      }
      toast.error(extractErrorMessage(err, 'Failed to update notification preference'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });

  // Toggling a channel writes the SAME value for the primary eventType and any
  // extraEventTypes so multi-key rows (e.g. birthdays + work anniversaries) stay in sync.
  const handleToggle = (item: CatalogItem, channel: ChannelKey, enabled: boolean) => {
    const eventTypes = [item.eventType, ...(item.extraEventTypes ?? [])];
    for (const eventType of eventTypes) {
      updatePref.mutate({ channel, eventType, enabled });
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary">
          <Bell className="h-5 w-5 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground">
            What we notify your team about, where, and when.
          </p>
        </div>
      </div>

      {isLoading ? (
        <Card className="flex items-center justify-center py-16">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </Card>
      ) : (
        <Card className="py-1.5">
          {NOTIFICATION_CATALOG.map((section, sectionIndex) => {
            const SectionIcon = section.icon;
            return (
              <div key={section.label}>
                <div
                  className={`flex items-center gap-2.5 px-5 pb-2 pt-4 ${
                    sectionIndex > 0 ? 'mt-1.5 border-t border-border/60' : ''
                  }`}
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                    <SectionIcon className="h-3.5 w-3.5 text-primary" />
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {section.label}
                  </span>
                </div>

                {section.items.map((item) => {
                  const schedule = item.scheduleKey
                    ? schedules.find((s) => s.key === item.scheduleKey)
                    : undefined;

                  return (
                    <div
                      key={item.eventType}
                      className="mx-2 flex items-center gap-4 rounded-xl px-3 py-3 transition-colors hover:bg-muted/45"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm">{item.name}</span>

                      <div className="flex flex-none items-center gap-4">
                        {CHANNELS.map((channel) => (
                          <label
                            key={channel.key}
                            className="flex cursor-pointer items-center gap-1.5"
                            title={channel.label}
                          >
                            <channel.icon className="h-3.5 w-3.5 text-muted-foreground" />
                            <Switch
                              checked={isEnabled(lookup, channel.key, item.eventType)}
                              onCheckedChange={(checked) =>
                                handleToggle(item, channel.key, checked)
                              }
                              aria-label={`${item.name} — ${channel.label}`}
                            />
                          </label>
                        ))}
                      </div>

                      <div className="flex w-[180px] flex-none justify-end">
                        {item.scheduleKey && canConfigure && schedule ? (
                          <SchedulePopover
                            schedule={schedule}
                            onChange={(body) =>
                              updateSchedule.mutate({ key: item.scheduleKey!, body })
                            }
                          />
                        ) : item.scheduleKey ? (
                          <span className="self-center text-sm text-muted-foreground/50">—</span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Card>
      )}

      {/* A broadcast to a chat room, not a per-person subscription: it is not in the
          catalog above because that list is backed by NotificationPreference, which is
          keyed by employeeId. This one is company-wide, so it gets its own card. */}
      {canConfigure && digest ? (
        <Card className="mt-6 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p id="whos-out-digest-label" className="text-sm font-medium">
                Daily who&apos;s-out digest
              </p>
              <p className="text-xs text-muted-foreground">
                Posts everyone who is off that day to the company Google Chat room, each
                working morning.
              </p>
            </div>
            <div className="flex flex-none items-center gap-3">
              <span id="whos-out-digest-time-label" className="sr-only">
                Daily who&apos;s-out digest send time
              </span>
              <SendHourSelect
                value={digest.sendTime}
                onChange={(sendTime) =>
                  updateSchedule.mutate({
                    key: 'whos_out_digest',
                    body: { leadDays: digest.leadDays, sendTime, enabled: digest.enabled },
                  })
                }
                labelId="whos-out-digest-time-label"
              />
              <Switch
                checked={digest.enabled}
                onCheckedChange={(enabled) =>
                  updateSchedule.mutate({
                    key: 'whos_out_digest',
                    body: { leadDays: digest.leadDays, sendTime: digest.sendTime, enabled },
                  })
                }
                aria-labelledby="whos-out-digest-label"
              />
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
